import { Check } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import { NeedHelpCard } from "@/v2/pages/FavoritesManager/components/right-rail/NeedHelpCard";
import { SidebarLegalFooter } from "@/v2/components/SidebarLegalFooter";
import type { GiftStep, StepInfo } from "../types";

export function GiftStepperSidebar({
  step,
  stepsList,
  onStepClick,
  supportUrl,
}: {
  step: GiftStep;
  stepsList: StepInfo[];
  onStepClick: (id: GiftStep) => void;
  supportUrl?: string;
}) {
  const currentStep = stepsList.find((st) => st.id === step);

  return (
    // Width/border/height are owned by the SidebarLayout column this renders
    // into (matches LensSidebar's contract) - fills whatever box the parent
    // gives it rather than sizing itself.
    <div className="w-full flex flex-col lg:h-full">
      {/* Below lg, SidebarLayout stacks this above the page's real content
          within a fixed-height viewport (no independent scrolling), so the
          full step list below - Need Help card and all - would eat the
          whole screen and squeeze the actual gift form to zero height. A
          compact progress bar keeps this slot short on mobile, matching
          LensSidebar's own collapsed-on-mobile contract. */}
      <div className="lg:hidden px-4 py-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            Step {step} of {stepsList.length}
          </span>
          <span className="truncate text-sm font-bold text-foreground">
            {currentStep?.title}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
          <div
            className="h-full rounded-full bg-action transition-all"
            style={{ width: `${(step / stepsList.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Scrollable independently of the footer below: min-h-0 overrides the
          flex item's default min-height:auto, which would otherwise let this
          grow to fit every step instead of respecting the bounded height and
          scrolling under it. */}
      <div className="hidden flex-col flex-1 min-h-0 lg:flex lg:overflow-y-auto p-4 pb-0">
        <div>
          <h2
            className="font-bold text-lg leading-snug tracking-tight mb-4 sm:mb-8"
            style={{ color: "var(--v2-foreground)" }}
          >
            Gift Subscription
          </h2>

          <div className="space-y-10">
            {stepsList.map((st, idx) => {
              const isDone = step > st.id;
              const isCurrent = step === st.id;
              // Once the purchase is confirmed (step 4) the flow is locked: no
              // stepping back to re-run payment, which would create a duplicate
              // gift + charge.
              const isNavigable = step < 4 && (isDone || isCurrent);

              return (
                <div key={st.id} className="relative">
                  {idx < stepsList.length - 1 && (
                    <div
                      className={cn(
                        "absolute left-3.5 top-7 w-0.5 h-12 transition-colors",
                        isDone ? "bg-action" : "bg-border",
                      )}
                    />
                  )}

                  <button
                    type="button"
                    disabled={!isNavigable}
                    onClick={() => isNavigable && onStepClick(st.id)}
                    className={cn(
                      "flex items-start gap-3.5 text-left w-full group bg-transparent",
                      isNavigable ? "cursor-pointer" : "cursor-default",
                    )}
                  >
                    <div
                      className={cn(
                        "size-7 rounded-full shrink-0 flex items-center justify-center font-bold text-xs transition-all",
                        isCurrent || isDone
                          ? "bg-action text-white"
                          : "bg-card text-muted-foreground border border-border",
                        isCurrent && "ring-4 ring-action/15",
                      )}
                    >
                      {isDone ? (
                        <Check className="size-3.5 stroke-[3]" />
                      ) : (
                        st.id
                      )}
                    </div>

                    <div className="pt-0.5 min-w-0 flex-1">
                      <p
                        className={cn(
                          "text-base font-bold tracking-tight leading-tight",
                          isCurrent || isDone
                            ? "text-foreground"
                            : "text-muted-foreground",
                        )}
                      >
                        {st.title}
                      </p>
                      <p className="text-sm truncate mt-2 text-muted-foreground">
                        {isDone ? st.subCompleted : st.subActive}
                      </p>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pinned to the bottom of the rail, outside the scrollable region
          above - stays visible even when the steps list needs to scroll,
          instead of scrolling away with it. Desktop only, like the list
          above it: the mobile compact bar has its own Need Help/legal access
          via the header's hamburger drawer and the SUPPORT nav tab. */}
      <div className="hidden flex-shrink-0 lg:block p-4">
        <div className="pt-6 sm:pt-8 border-t border-border">
          <NeedHelpCard href={supportUrl || "#"} />
        </div>
        <div className="pt-4">
          <SidebarLegalFooter />
        </div>
      </div>
    </div>
  );
}
